import G from "./Glob.mjs";

/**
 * @private
 * @protected
 * @type {string}
 */
const activeGroupKey = 'userActiveGroup';

/**
 * @private
 * @protected
 * @type {string}
 */
const noAuthBackendURI = G.prop.uri.api.replace(/\/[^/]+\/$/,"/chPasswd/");

/**
 * @private
 * @protected
 * @type {{groups: {[p:string]:any}, [p:string]:any}}
 */
const cache = { groups: {} };

const appLib = {

	/**
	 * Helper function for case-insensitive sort
	 * @param {string} a
	 * @param {string} b
	 * @return {1|-1}
	 */
	ciSort: (a,b) => (a.toLocaleLowerCase() > b.toLocaleLowerCase() ? 1 : -1),

	/**
	 * Remove groups-related cache
	 * @return {void}
	 */
	clearCache: ()=> { cache.groups = {} },

	/**
	 * Set/Get ID of current active group of the user
	 * @function
	 * @param { string|false } [groupID] If provided sets group
	 * @return {string|undefined}
	 * @see appLib.onGroupChange
	 */
	group: ( groupID) =>  {
		if ( groupID === false ) {
			G.setStatus(activeGroupKey);
			G.rmCurrentPageParam('group');
			return undefined;
		}
		if ( groupID && typeof groupID === 'string' ) {
			if (groupID !== G.getStatus(activeGroupKey)) G.setStatus(activeGroupKey, groupID );
			return groupID;
		}
		return G.getStatus(activeGroupKey) ||  G.getPageParam('group') || undefined;
	},

	/**
	 * Register to group change event (on activeGroupKey status variable)
	 * @desc Glob.deregister will unregister this as well.
	 * @param {function} handler
	 * @param {string} [uid]
	 * @return {function} Unregister function
	 * @see appLib.group
	 * @see Glob.register
	 * @see Glob.deregister
	 */
	onGroupChange: ( handler, uid ) => G.register(activeGroupKey, handler, uid ),

	/**
	 * Full list of items defined ATECO codes
	 * @param {boolean} [refresh] Should the list be refreshed?
	 * @return { Promise<object> }
	 */
	getAtecoList : ( refresh= false ) => {
		return new Promise((resolve, reject) => {
			if ( cache.atecoList && ! refresh ) return resolve(G.clone( cache.atecoList ));
			G.api('getAtecoList')
				.then( data => {
					cache.atecoList = data || {};
					resolve(G.clone( cache.atecoList ));
			}).catch( e => {
				reject( e );
			})
		});
	},

	/**
	 * Get info for form definition
	 * @param {string} [groupID] Defaults to current active group
	 * @param {boolean} [refetch] Re-fetch even if cached
	 * @return {Promise<{lastUpdate: number, formDefinition: object, group: object}>}
	 */
	getFormInfo: async (groupID, refetch ) => {
		if ( typeof groupID === 'undefined' ) groupID = appLib.group();
		if ( ! groupID ) return undefined;
		if ( ! cache.groups[groupID] ) cache.groups[groupID] = {};
		if ( cache.groups[groupID].formUpdate && ! refetch ) {
			return {
				lastUpdate: cache.groups[groupID].formUpdate,
				formDefinition: G.clone(cache.groups[groupID].form),
				group: G.clone(cache.groups[groupID].info)
			}
		}
		const fi = await G.api('getFormInfo', { id: groupID });
		if ( fi?.lastUpdate ) {
			cache.groups[groupID].info = fi.group;
			cache.groups[groupID].form = fi.formDefinition;
			cache.groups[groupID].formUpdate = fi.lastUpdate;
		}
		return G.clone(fi);
	},

	/**
	 * Get group info
	 * @param {string} [groupID] Defaults to current active group
	 * @param {boolean} [refetch] Re-fetch even if cached
	 * @return {Promise<Object>}
	 */
	getGroupInfo: async ( groupID, refetch ) => {
		if ( typeof groupID === 'undefined' ) groupID = appLib.group();
		if ( ! groupID ) return undefined;
		if ( ! cache.groups[groupID] ) cache.groups[groupID] = {};
		if ( cache.groups[groupID].info && ! refetch ) return G.clone(cache.groups[groupID].info);
		const gi = await G.api('getGroupInfo', { group: groupID });
		return G.clone(cache.groups[groupID].info = gi );
	},


	/**
	 * Get data for an item
	 * @param {string} itemID The ID of the item
	 * @param {string} [groupID] Defaults to current active group
	 * @return {Promise<Object>}
	 */
	getItemData: async (itemID, groupID = appLib.group()) => {
		return (await G.api('getItem', { group: groupID, item: itemID }))
	},

	/**
	 * Full list of items defined for a given group
	 * @param {string} [groupID] The group ID, defaults to active group
	 * @param {boolean} [refetch] Force refetch
	 * @return { Promise<Object[]> }
	 */
	getItemsList : ( groupID= appLib.group(), refetch ) => {
		return new Promise((resolve, reject) => {
			if ( ! cache.groups[groupID] ) cache.groups[groupID] = {};
			if ( cache.groups[groupID].items && ! refetch ) return resolve( G.clone( cache.groups[groupID].items ) );
			G.api( 'listItems', { group: groupID } )
				.then( jdata => {
					cache.groups[groupID].items = Object.entries(jdata||{})
						.map( ([k,v]) => {
							v.id = k;
							return v;
						})
						.filter( v => (!! v?.name && ! v?.deleted) )
						.sort( (a,b) => appLib.ciSort(a.name, b.name) )
					;
					resolve( G.clone( cache.groups[groupID].items ) );
				}).catch( e => {
					reject( e );
				});
		});
	},

	/**
	 * Rename an item
	 * @param {string} newName Item new name
	 * @param {string} itemID The ID of the item
	 * @param {string} [groupID] Defaults to current active group
	 * @return {Promise<Object>}
	 */
	renameItem: ( newName, itemID, groupID = appLib.group() ) => {
		return new Promise((resolve, reject) => {
			G.api(
				`renameItem/${groupID}/${itemID}`,
				{ name: newName }
			).then( jdata => {
				delete cache.groups[groupID].items;
				resolve( jdata );
			}).catch( e=> {
				reject( e );
			})
		})
	},

	/**
	 * Remove an item
	 * @param {string} itemID The ID of the item
	 * @param {string} [groupID] Defaults to current active group
	 * @return {Promise<Object>}
	 */
	rmItem: ( itemID, groupID = appLib.group() ) => {
		return new Promise((resolve, reject) => {
			G.api(
				`rmItem/${groupID}/${itemID}`,
				{ group: groupID, item: itemID }
			).then( jdata => {
				delete cache.groups[groupID].items;
				resolve( jdata );
			}).catch( e=> {
				reject( e );
			})
		})
	},

	/**
	 * Set data for an item
	 * @param { object } data The data to be saved
	 * @param {string} [itemID] The ID of the item, defaults to data.id
	 * @param {string} [groupID] Defaults to current active group
	 * @return {Promise<Object>}
	 */
	setItemData: ( data, itemID, groupID = appLib.group() ) => {
		if ( ! itemID ) itemID = data?.id;
		return new Promise( (resolve, reject) => {
			if ( ! itemID ) return reject( 'setItemData: itemID is missing' );
			if ( ! groupID ) return reject( 'setItemData: groupID is missing' );
			G.api(
				`setItem/${groupID}/${itemID}`,
				data
			).then( jdata => {
				delete cache.groups[groupID].items;
				resolve(jdata);
			}).catch( e => {
				reject( e );
			});
		});
	},

	/**
	 * Ask for a token to reset forgot password
	 * @param {string} user User ID or email
	 */
	askToken: ( user ) => {
			/** @type { string } */
			const uri = noAuthBackendURI + "sendToken";
			/** @type { Request } */
			const req = {
				cache: 'no-cache',
				method: 'POST',
				body: JSON.stringify({ user: user } ),
				headers: {
					'Content-Type': 'application/json; charset=UTF-8',
					accept: 'application/json; charset=UTF-8, application/xml; charset=UTF-8',
					pragma: 'no-cache',
					'cache-control': 'no-cache'
				}
			};
			return new Promise( ( resolve, reject ) => {
				fetch(
					uri, req
				).then( response => {
					if ( response.ok ) return response.json();
					reject (new Error(String(response.status))+' '+response.statusText);
				}).then( data => {
					resolve( data );
				}).catch( e => {
					reject(e);
				})
			});
		},

	/**
	 * Change password by token
	 * @param {string} user User ID or email
	 * @param {string} token
	 * @param {string} password
	 */
	chPwdByToken: ( user, token, password ) => {
		/** @type { string } */
		const uri = noAuthBackendURI + "verifyToken";
		/** @type { Request } */
		const req = {
			cache: 'no-cache',
			method: 'POST',
			body: JSON.stringify({ user: user, token: token, password: password } ),
			headers: {
				'Content-Type': 'application/json; charset=UTF-8',
				accept: 'application/json; charset=UTF-8, application/xml; charset=UTF-8',
				pragma: 'no-cache',
				'cache-control': 'no-cache'
			}
		};
		return new Promise( ( resolve, reject ) => {
			fetch(
				uri, req
			).then( response => {
				if ( response.ok ) return response.json();
				reject (new Error(String(response.status))+' '+response.statusText);
			}).then( data => {
				resolve( data );
			}).catch( e => {
				reject(e);
			})
		});
	}

};

export default appLib;